import { useCallback, useEffect, useRef, useState } from "react";

import { applicantHistoryApi } from "../../../api/applicantHistoryApi";

/**
 * One applicant-history fetch per loan, shared by every applicant card (the
 * response already covers every borrower on the deal).
 *
 * `borrowersKey` should change when the set of borrowers changes — the backend
 * recomputes when its stored fingerprint no longer matches, so re-asking is
 * enough to pick up an added or edited applicant.
 *
 * A 404 means either the deal isn't found or (more likely during rollout) the
 * route isn't deployed on this backend yet — the panel treats both as
 * "unavailable" and hides itself rather than showing an error on every card.
 */
export const useApplicantHistory = (dealId, borrowersKey) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  // Drop a response that lands after the loan (or borrowers) changed.
  const generation = useRef(0);

  const load = useCallback(
    ({ refresh = false } = {}) => {
      if (!dealId) return;
      const gen = ++generation.current;
      if (refresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      applicantHistoryApi
        .forDeal(dealId, { refresh })
        .then((res) => {
          if (gen !== generation.current) return;
          setData(res || null);
          setUnavailable(false);
        })
        .catch((err) => {
          if (gen !== generation.current) return;
          if (err?.response?.status === 404) {
            setUnavailable(true);
            return;
          }
          setError(
            err?.response?.data?.detail ||
              err?.response?.data?.error ||
              "Couldn't check prior loans.",
          );
        })
        .finally(() => {
          if (gen !== generation.current) return;
          setLoading(false);
          setRefreshing(false);
        });
    },
    [dealId],
  );

  // A different loan starts blank; a borrower edit on the same loan keeps the
  // current result on screen while the fresh one loads (no spinner flash).
  useEffect(() => {
    setData(null);
    setUnavailable(false);
  }, [dealId]);

  useEffect(() => {
    load();
  }, [load, borrowersKey]);

  const refresh = useCallback(() => load({ refresh: true }), [load]);

  return { data, loading, refreshing, error, unavailable, refresh };
};

export default useApplicantHistory;
